/*
 * SVGO is a library that standardizes the rewriting of an SVG file into a different format.
 * It has a plugin architecture and comes with many prebuilt plugins.
 *
 * In addition we can write our own plugins, which are in the svgo-plugins folder.
 *
 * In this file we are simplifying an SVG into a format that's easy to convert to "PIXI JSON" which is
 * the format used by our SDK 2.0 applications.
 *
 * In particular, this format means:
 *
 * - the only "shapes" in the output SVG must be "paths"
 * - only M, L or C "instructions" (Move, LineTo, CurveTo) are allowed in any path
 *
 * @see https://github.com/svg/svgo
 */

import Svgo                              from 'svgo'
import { lengthenFillColor }             from './svgo-plugins/lengthen-fill-color'
import { convertShapeToPath }            from './svgo-plugins/convert-shape-to-path'
import { simplifyPathToOnlyMlc }         from './svgo-plugins/simplify-path-to-only-mlc'
import { validateCompatibilityWithPixi } from './svgo-plugins/validate-compatibility-with-pixi'
import { fixSubpathsForPixi }            from './svgo-plugins/fix-subpaths-for-pixi'

/*
 * Convert an svg to a POJO, simplify that, than reverse the conversion
 * @sig simplySvg :: String -> String
 */
const simplifySvgForPixi = async svg => (await new Svgo({
    full: true,
    plugins: [
        { cleanupAttrs                   : true },
        { cleanupEnableBackground        : true },
        { cleanupIDs                     : true },
        { cleanupListOfValues            : true },
        { cleanupNumericValues           : { floatPrecision: 2 } },
        { collapseGroups                 : true },  // doesn't always work fully? eg. baggage.wrap ends up with extra g
        { convertColors                  : { shorthex: false, shortname : false } },
        { lengthenFillColor },
        {
            convertPathData: {
                applyTransforms       : true,
                applyTransformsStroked: true,
                makeArcs              : {
                    threshold: 2.5, // coefficient of rounding error
                    tolerance: 0.5,  // percentage of radius
                },
                straightCurves        : true,
                lineShorthands        : true,
                curveSmoothShorthands : true,
                floatPrecision        : 2,
                transformPrecision    : 5,
                removeUseless         : true,
                collapseRepeated      : true,
                utilizeAbsolute       : true,
                leadingZero           : true,
                negativeExtraSpace    : true,
                forceAbsolutePath     : true,
            },
        },
        { convertStyleToAttrs            : true },
        { convertTransform               : true },
        { inlineStyles                   : { onlyMatchedOnce: false } },
        { mergePaths                     : false },
        { minifyStyles                   : false },  // forces #FFFFFF to #FFF -- which we don't want
        { moveElemsAttrsToGroup          : false },  // we want to get rid of groups, not push stuff into them...
        { moveGroupAttrsToElems          : true },   // ...this one's what we want!
        { prefixIds                      : true },
        { removeComments                 : true },
        { removeDesc                     : true },
        { removeDimensions               : true },
        { removeDoctype                  : true },
        { removeEditorsNSData            : true },
        { removeElementsByAttr           : true },
        { removeEmptyAttrs               : true },
        { removeEmptyContainers          : true },
        { removeEmptyText                : true },
        { removeHiddenElems              : true },
        { removeMetadata                 : true },
        { removeNonInheritableGroupAttrs : true },
        { removeOffCanvasPaths           : false },
        { removeRasterImages             : true },
        { removeScriptElement            : true },
        { removeStyleElement             : true },
        { removeTitle                    : true },
        { removeUnknownsAndDefaults      : true },
        { removeUnusedNS                 : true },
        { removeUselessDefs              : true },
        { removeUselessStrokeAndFill     : true },
        { removeViewBox                  : true },
        { sortAttrs                      : true },

        { convertShapeToPath },
        { simplifyPathForPixi: simplifyPathToOnlyMlc },
        { simplifySubpaths: fixSubpathsForPixi },

        // pointless?
        { addAttributesToSVGElement  : false },
        { addClassesToSVGElement     : false },
        { removeAttributesBySelector : false },
        { removeAttrs                : false },

        // crashes in conversion or produces incorrect output for some svg
        { reusePaths        : false },      // crashes
        { removeXMLNS       : false },
        { removeXMLProcInst : false },

        { validateCompatibilityWithPixi },
    ],
    js2svg : { pretty: true },
}).optimize(svg)).data

export default simplifySvgForPixi
